import { z } from 'zod';

const Theme = z.enum(['light', 'dark', 'system']);
const Density = z.enum(['compact', 'default', 'comfortable']);
const TimeFormat = z.enum(['12h', '24h']);
const WeekStart = z.enum(['mon', 'sun']);
/**
 * Mobile landing tab, as stored (`users.mobile_default_tab`). The ids are
 * internal and never change; only their labels do (glossary: `dashboard` =
 * Home, `tasks` = My Work, `chat` = Chat, `workspace` = Projects).
 *
 * `auto` (the default for new users since migration 0116) = My Work, or Home
 * when I'm Lead or above in at least one regular team (§5.2). The server
 * resolves it: see `UserPrivate.mobileLandingTab`.
 */
const MobileDefaultTab = z.enum(['auto', 'dashboard', 'tasks', 'chat', 'workspace']);
/** A concrete landing tab — what `auto` resolves to (never `auto` itself). */
const MobileLandingTab = MobileDefaultTab.exclude(['auto']);
export const WeekStartSchema = WeekStart;
export const MobileDefaultTabSchema = MobileDefaultTab;
export const MobileLandingTabSchema = MobileLandingTab;
export type MobileLandingTabValue = z.infer<typeof MobileLandingTab>;
/** Discovery & Messaging (Sprint 1.2). */
export const WhoCanFindMe = z.enum(['everyone', 'teams', 'exact', 'nobody']);
export const WhoCanMessage = z.enum(['anyone', 'contacts', 'requests']);
export type TimeFormatValue = z.infer<typeof TimeFormat>;
export type WeekStartValue = z.infer<typeof WeekStart>;
export type MobileDefaultTabValue = z.infer<typeof MobileDefaultTab>;
export type WhoCanFindMeValue = z.infer<typeof WhoCanFindMe>;
export type WhoCanMessageValue = z.infer<typeof WhoCanMessage>;

/**
 * Curated, on-brand avatar background colours for the initials fallback (when
 * a user hasn't uploaded an image).  A fixed small set so it stays tasteful and
 * consistent; `null` = the neutral default.  The hex/text values live on the
 * web side (`shared/lib/avatar-colors.ts`) keyed by these ids.
 */
export const AVATAR_COLOR_IDS = [
  'neutral',
  'coral',
  'amber',
  'slate',
  'teal',
  'indigo',
  'rose',
] as const;
export const AvatarColorIdSchema = z.enum(AVATAR_COLOR_IDS);
export type AvatarColorId = z.infer<typeof AvatarColorIdSchema>;

// What every API response returns about a user *visible to others* — public projection.
export const UserPublicSchema = z.object({
  id: z.string().uuid(),
  username: z.string(),
  name: z.string(),
  avatarUrl: z.string().nullable(),
  /** Chosen initials-avatar colour id (see AVATAR_COLOR_IDS); null = neutral. */
  avatarColor: AvatarColorIdSchema.nullable().optional(),
  bio: z.string().nullable(),
  isBot: z.boolean(),
});
export type UserPublic = z.infer<typeof UserPublicSchema>;

/**
 * Richer projection for the profile drawer — adds public-ish extras (timezone,
 * github login, join date) plus viewer-relative context so the UI can choose
 * affordances (Add contact vs Remove, Open DM vs Start DM, etc.) without an
 * extra round-trip.
 *
 * Visibility: the caller's `UsersService.getById` enforces "viewer can see iff
 * self / discoverable / shared team / DM / contact" so anonymous lookups stay
 * private.
 */
export const UserProfileSchema = UserPublicSchema.extend({
  timezone: z.string(),
  githubLogin: z.string().nullable(),
  joinedAt: z.string(),
  /** Viewer-relative — null when viewer is the same user as the subject. */
  isSelf: z.boolean(),
  isContact: z.boolean(),
  hasOpenDm: z.boolean(),
  sharedTeams: z.array(
    z.object({
      teamId: z.string().uuid(),
      teamSlug: z.string(),
      teamName: z.string(),
    }),
  ),
});
export type UserProfile = z.infer<typeof UserProfileSchema>;

// /me — full self-view including settings.
export const UserPrivateSchema = UserPublicSchema.extend({
  email: z.string(),
  emailVerifiedAt: z.date().nullable(),
  timezone: z.string(),
  /** The UI language the person chose; null = not chosen (the web app follows the browser). */
  locale: z.string().nullable(),
  /** The UI language the web app last detected while `locale` is null (server copy only). */
  detectedLocale: z.string().nullable().optional(),
  theme: Theme,
  density: Density,
  timeFormat: TimeFormat,
  weekStart: WeekStart,
  /** Mobile shell landing tab as chosen (`auto` = decided by the server, see below). */
  mobileDefaultTab: MobileDefaultTab,
  /**
   * The concrete tab the mobile shell lands on: `mobileDefaultTab` itself,
   * or — for `auto` — `tasks` (My Work), or `dashboard` (Home) when I'm Lead
   * or above in any regular team. Desktop always lands on Home. Optional so an
   * older server still parses; the client then treats `auto` as `tasks`.
   */
  mobileLandingTab: MobileLandingTab.optional(),
  discoverableByUsername: z.boolean(),
  discoverableByEmail: z.boolean(),
  /** Discovery & Messaging (Sprint 1.2). */
  whoCanFindMe: WhoCanFindMe,
  whoCanMessage: WhoCanMessage,
  /** Sprint 2.3 — first-run checklist dismissed forever (survives devices). */
  onboardingDismissed: z.boolean(),
  twoFactorEnabled: z.boolean(),
  githubLogin: z.string().nullable(),
  /** Operator/admin flag — drives apps/admin auth gate and customer-side admin chrome. */
  isSuperadmin: z.boolean(),
  createdAt: z.date(),
  updatedAt: z.date(),
  /**
   * Plan-feature entitlements resolved for THIS user — true when the personal
   * plan OR any active team membership grants it. Lets the FE lock paid-only
   * surfaces (e.g. the API-tokens tab) straight from `/api/me`, no 2nd request.
   */
  entitlements: z.object({
    public_api: z.boolean(),
  }),
});
export type UserPrivate = z.infer<typeof UserPrivateSchema>;

export const UpdateProfileSchema = z.object({
  name: z.string().min(1).max(80).optional(),
  /** Change the username (must stay unique, case-insensitive). */
  username: z
    .string()
    .regex(/^[a-zA-Z0-9_-]{2,32}$/, 'Username must be 2-32 chars: letters, digits, _ or -')
    .optional(),
  bio: z.string().max(500).nullable().optional(),
  avatarUrl: z.string().url().nullable().optional(),
  /** Initials-avatar colour choice; null clears it back to neutral. */
  avatarColor: AvatarColorIdSchema.nullable().optional(),
  timezone: z.string().max(64).optional(),
  /** An explicit language choice; null clears it (back to the browser language). */
  locale: z.string().max(8).nullable().optional(),
  /** What the web app detected while no language is chosen — never a choice. */
  detectedLocale: z.string().max(8).optional(),
  theme: Theme.optional(),
  density: Density.optional(),
  timeFormat: TimeFormat.optional(),
  weekStart: WeekStart.optional(),
  mobileDefaultTab: MobileDefaultTab.optional(),
  discoverableByUsername: z.boolean().optional(),
  discoverableByEmail: z.boolean().optional(),
  whoCanFindMe: WhoCanFindMe.optional(),
  whoCanMessage: WhoCanMessage.optional(),
  /** Sprint 2.3 — dismiss/restore the first-run checklist forever. */
  onboardingDismissed: z.boolean().optional(),
});
export type UpdateProfileInput = z.infer<typeof UpdateProfileSchema>;

export const SearchUsersQuerySchema = z.object({
  /** Search term — matched against username, name, and email (the last
   *  honours `discoverableByEmail`). */
  q: z.string().min(1).max(254),
  /** Result cap.  Default 10, hard ceiling 50. */
  limit: z.coerce.number().int().min(1).max(50).optional(),
  /** When set, hides users already in the actor's contacts list.  Useful
   *  for the "Add contact" flow so duplicates don't crowd the result. */
  excludeContacts: z
    .union([z.boolean(), z.string()])
    .transform((v) => v === true || v === 'true' || v === '1')
    .optional(),
});
export type SearchUsersQuery = z.infer<typeof SearchUsersQuerySchema>;

export const SessionItemSchema = z.object({
  id: z.string().uuid(),
  userAgent: z.string().nullable(),
  ipAddress: z.string().nullable(),
  createdAt: z.date(),
  lastUsedAt: z.date(),
  expiresAt: z.date(),
  isCurrent: z.boolean(),
});
export type SessionItem = z.infer<typeof SessionItemSchema>;

export const SessionListSchema = z.array(SessionItemSchema);
export type SessionList = z.infer<typeof SessionListSchema>;
